import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { lastLocationApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Paths we never want to "resume" into — auth pages, the bare root,
// and any path explicitly opted out via a `?noresume` query flag.
const IGNORED_PATTERNS = [
  /^\/login(\/|$|\?)/,
  /^\/forgot-password(\/|$|\?)/,
  /^\/reset-password(\/|$|\?)/,
  /^\/signup(\/|$|\?)/,
  /^\/$/,
  /^\/index\.html(\/|$|\?)/,
];

function shouldTrack(pathname) {
  return !IGNORED_PATTERNS.some((re) => re.test(pathname));
}

/**
 * Silent component — listens to route changes and PUTs `/auth/last-location`
 * to the backend so the next login can resume the same workspace.
 * Debounced at 1.5s so transient navigation (e.g. bouncing through a
 * redirect) doesn't thrash the API. Tracks EVERY meaningful page (not
 * just patient/form pages) so the resume-on-login always reflects where
 * the user actually left off.
 */
export default function LastLocationTracker() {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const timerRef = useRef(null);
  const lastSentRef = useRef('');

  useEffect(() => {
    if (!user || user === false) return;
    if (!shouldTrack(pathname)) return;
    const full = pathname + (search || '');
    if (full === lastSentRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        // Best-effort label — the backend only uses it for display.
        let label = document.title || '';
        // Strip site suffix like " · Savenes" when present
        label = label.replace(/\s*[·|]\s*Savenes.*$/i, '').trim();
        await lastLocationApi.update({ path: full, label });
        lastSentRef.current = full;
      } catch { /* ignore — tracking is non-critical */ }
    }, 1500);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pathname, search, user]);

  return null;
}
